import { useMemo, useState } from "react";
import { CarFront, CircleCheck, Clock3, CreditCard, MessageCircle, Phone, ShieldCheck, Smartphone, Save } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import BrandHeader from "@/components/BrandHeader";
import SoftButton from "@/components/SoftButton";
import { getSafeMode, saveSafeMode } from "@/lib/safeMode";

const CHOICES = [
  { id: "protectChoices", title: "Protect my choices", note: "Add friction before texting, calling, shopping, posting, or other decisions you may want space around.", icon: Smartphone },
  { id: "protectFinances", title: "Protect finances", note: "Plan a spending pause or open your bank/card controls to lock a card while keeping essentials in mind.", icon: CreditCard },
  { id: "getHome", title: "Help me get home", note: "Keep transport and an exit plan easy to reach.", icon: CarFront },
  { id: "callPerson", title: "Call my person", note: "Keep one trusted person close for the night or a high-risk moment.", icon: Phone },
];

export default function SafeMode() {
  const [value, setValue] = useState(() => getSafeMode());

  const enabled = useMemo(() => CHOICES.filter((item) => value[item.id]), [value]);
  const toggle = (id) => setValue((current) => ({ ...current, [id]: !current[id] }));
  const save = () => {
    let next;
    try { next = saveSafeMode(value); } catch { toast.error("Your plan could not be saved. Keep this screen open or copy your notes."); return; }
    setValue(next);
    toast.success("Plan saved on this device.", { description: "A little more space between impulse and action." });
  };

  const call = () => {
    if (!/^\+?[0-9 ()-]{3,30}$/.test(value.contactPhone)) { toast.error("Enter a valid phone number first."); return; }
    window.location.href = `tel:${value.contactPhone}`;
  };

  const text = (message = "Could you check in with me tonight? I may want a little support getting home or slowing things down.") => {
    if (!/^\+?[0-9 ()-]{3,30}$/.test(value.contactPhone)) { toast.error("Enter a valid phone number first."); return; }
    const body = encodeURIComponent(message);
    window.location.href = `sms:${value.contactPhone}?&body=${body}`;
  };

  return (
    <PageContainer className="pt-7" testid="safe-mode-page">
      <BrandHeader compact />

      <section className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.34em] mb-2" style={{ color: "var(--un-text-3)" }}>Safer Tonight</p>
        <h1 className="text-[43px] leading-[1.01] tracking-[-0.035em]" style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}>
          Give yourself a little more support tonight
          <br />
          <em className="font-normal" style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}>without judging it.</em>
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>
          If tonight might feel harder to navigate than usual, you can set up a few supports now.
        </p>
      </section>

      <section className="rounded-[1.75rem] p-5 mb-5" style={{ background: "rgba(74,107,93,0.10)", border: "1px solid rgba(74,107,93,0.12)" }}>
        <div className="flex gap-3 items-start">
          <ShieldCheck size={20} className="mt-0.5" style={{ color: "var(--un-safe)" }} />
          <div>
            <p className="text-[15px] font-medium" style={{ color: "var(--un-text-1)" }}>Less harm. More agency.</p>
            <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
              Safer Tonight helps you make a plan for pauses and support. It does not block apps, lock cards, monitor you, or contact anyone automatically. It does not make drinking, spending, or any other choice risk-free, and it does not replace emergency help.
            </p>
          </div>
        </div>
      </section>

      <div className="space-y-3 mb-5">
        {CHOICES.map((item) => {
          const Icon = item.icon;
          const active = Boolean(value[item.id]);
          return (
            <button key={item.id} onClick={() => toggle(item.id)} aria-pressed={active} className="w-full un-card p-5 text-left un-press" style={{ border: active ? "1px solid rgba(91,57,84,0.18)" : undefined }}>
              <div className="flex gap-3 items-start">
                <span className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ background: active ? "var(--un-plum)" : "rgba(91,57,84,0.06)", color: active ? "#fffaf5" : "var(--un-plum)" }}>
                  <Icon size={18} />
                </span>
                <div className="flex-1">
                  <p className="text-[16px] font-medium" style={{ color: "var(--un-text-1)" }}>{item.title}</p>
                  <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>{item.note}</p>
                </div>
                <span className="text-[11px] rounded-full px-2 py-1" style={{ background: active ? "rgba(74,107,93,0.12)" : "rgba(91,57,84,0.05)", color: active ? "var(--un-safe)" : "var(--un-text-3)" }}>
                  {active ? "On my plan" : "Add to plan"}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {value.callPerson && (
        <section className="un-card p-5 mb-5">
          <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--un-text-3)" }}>Night Contact</p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <input
              value={value.contactName}
              onChange={(e) => setValue((current) => ({ ...current, contactName: e.target.value }))}
              placeholder="Name"
              className="min-w-0 w-full rounded-2xl p-3 outline-none text-[14px]"
              style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            />
            <input
              value={value.contactPhone}
              onChange={(e) => setValue((current) => ({ ...current, contactPhone: e.target.value }))}
              placeholder="Phone"
              inputMode="tel"
              className="min-w-0 w-full rounded-2xl p-3 outline-none text-[14px]"
              style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <SoftButton variant="secondary" size="md" onClick={call} disabled={!value.contactPhone} iconLeft={<Phone size={14} />}>Call</SoftButton>
            <SoftButton variant="secondary" size="md" onClick={() => text()} disabled={!value.contactPhone} iconLeft={<MessageCircle size={14} />}>Text</SoftButton>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 mt-3">
            {[
              ["I’m okay", "I’m okay. Just checking in like we planned."],
              ["I’m heading home", "I’m heading home now. I’ll let you know when I’m in."],
              ["Call me", "Can you call me when you see this? I’d like a little support right now."],
              ["Stay with me", "Could you stay with me for a bit? I’d like some company and support right now."],
              ["I made it home", "I made it home. Thank you for being there."],
            ].map(([label, message]) => (
              <button
                key={label}
                onClick={() => {
                  if (label === "I made it home") {
                    try {
                      const next = saveSafeMode({ ...value, madeItHomeAt: new Date().toISOString() });
                      setValue(next);
                    } catch {
                      toast.error("Your arrival note could not be saved on this device.");
                    }
                  }
                  text(message);
                }}
                disabled={!value.contactPhone}
                className="shrink-0 rounded-full px-3 py-2 text-[11px] un-press disabled:opacity-40"
                style={{ background: "rgba(91,57,84,0.05)", color: "var(--un-plum)" }}
              >
                {label}
              </button>
            ))}
          </div>
          {value.madeItHomeAt && (
            <p className="mt-3 text-[10px]" style={{ color: "var(--un-text-3)" }}>
              Made-it-home note saved {new Date(value.madeItHomeAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}.
            </p>
          )}
        </section>
      )}

      <section className="rounded-[1.75rem] p-5 mb-5" style={{ background: "rgba(91,57,84,0.035)", border: "1px solid rgba(91,57,84,0.06)" }}>
        <button
          onClick={() => setValue((current) => ({ ...current, saferTonight: !current.saferTonight }))}
          className="w-full flex items-start gap-3 text-left un-press"
          aria-pressed={Boolean(value.saferTonight)}
        >
          <span className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ background: value.saferTonight ? "rgba(74,107,93,0.13)" : "rgba(91,57,84,0.05)", color: value.saferTonight ? "var(--un-safe)" : "var(--un-plum)" }}>
            <CircleCheck size={18} />
          </span>
          <span className="flex-1">
            <span className="block text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--un-text-3)" }}>Optional companion</span>
            <span className="block mt-1 text-[15px] font-medium" style={{ color: "var(--un-text-1)" }}>Safer Tonight</span>
            <span className="block mt-1 text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
              If tonight may be harder to navigate, you can set an intention, a check-in, and a practical exit plan. No judgment attached.
            </span>
          </span>
          <span className="text-[11px] rounded-full px-2 py-1" style={{ background: value.saferTonight ? "rgba(74,107,93,0.12)" : "rgba(91,57,84,0.05)", color: value.saferTonight ? "var(--un-safe)" : "var(--un-text-3)" }}>
            {value.saferTonight ? "On my plan" : "Add to plan"}
          </span>
        </button>

        {value.saferTonight && (
          <div className="mt-4 space-y-3">
            <textarea
              value={value.nightIntention}
              onChange={(e) => setValue((current) => ({ ...current, nightIntention: e.target.value }))}
              placeholder="What would ‘safer and still enjoyable’ look like for you tonight?"
              rows={2}
              className="w-full resize-none rounded-2xl p-3 outline-none text-[13px] leading-relaxed"
              style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            />
            <div className="grid grid-cols-2 gap-2">
              <label className="rounded-2xl p-3" style={{ background: "var(--un-bg-2)" }}>
                <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--un-text-3)" }}><Clock3 size={12} /> Check-in</span>
                <input
                  type="time"
                  value={value.checkInAt}
                  onChange={(e) => setValue((current) => ({ ...current, checkInAt: e.target.value }))}
                  className="mt-2 w-full bg-transparent outline-none text-[13px]"
                  style={{ color: "var(--un-text-1)" }}
                />
              </label>
              <textarea
                value={value.essentialsNote}
                onChange={(e) => setValue((current) => ({ ...current, essentialsNote: e.target.value }))}
                placeholder="Ride / food / meds / essentials"
                rows={2}
                className="w-full resize-none rounded-2xl p-3 outline-none text-[12px] leading-relaxed"
                style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
              />
            </div>
            <p className="text-[10px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
              Mixing alcohol with some medicines or other substances can raise risk. If something feels medically wrong, use emergency help rather than relying on the app.
            </p>
          </div>
        )}
      </section>

      <p className="text-xs mb-5" style={{ color: "var(--un-text-3)" }}>Check-in time is a note only. Set an alarm yourself; no reminder or automatic contact is scheduled.</p>
      {value.getHome && <section className="un-card p-5 mb-5"><h2>Getting home</h2><p className="text-sm mt-2">Choose a trusted sober driver, taxi or ride service before you go. Keep transport money accessible. This app does not book or track a ride.</p><textarea aria-label="Getting home plan" value={value.transportPlan || ""} onChange={(e) => setValue((current) => ({ ...current, transportPlan: e.target.value }))} className="w-full rounded-2xl p-3 mt-3" placeholder="My ride and backup plan" /></section>}
      {value.protectChoices && <p className="text-sm mb-5">For a pause before sending, use <a className="underline" href="/drafts">Drafts on hold</a>. Use your device settings for app limits.</p>}
      {value.protectFinances && (
        <section className="rounded-[1.75rem] p-5 mb-5" style={{ background: "rgba(214,184,132,0.10)", border: "1px solid rgba(196,161,106,0.14)" }}>
          <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--un-text-3)" }}>Protect finances</p>
          <p className="mt-2 text-[15px] font-medium" style={{ color: "var(--un-text-1)" }}>Pause spending without stranding yourself.</p>
          <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
            Keep enough access for transportation, food, lodging, medication, or other essentials. Direct card locking depends on your issuer; this app records your plan and cannot lock a card or move money.
          </p>
        </section>
      )}

      <textarea
        value={value.note}
        onChange={(e) => setValue((current) => ({ ...current, note: e.target.value }))}
        placeholder="Anything you want tonight-you to remember? (optional)"
        rows={3}
        className="w-full resize-none rounded-2xl p-4 outline-none text-[14px] leading-relaxed mb-4"
        style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
      />

      <SoftButton variant="primary" size="lg" className="w-full mb-3" iconLeft={<Save size={16} />} onClick={save}>
        Save my plan
      </SoftButton>
      <p className="text-center text-[10px] leading-relaxed px-7 mb-8" style={{ color: "var(--un-text-3)" }}>
        {enabled.length} part{enabled.length === 1 ? "" : "s"} of your plan selected. You remain in control and can change them anytime.
      </p>
    </PageContainer>
  );
}
